/**
 * MODULE: domain/sales-intelligence/frontend/hooks/useAccountActions.ts
 * PURPOSE: Hook for save-to-account, export PDF, and share profile actions.
 * DEPENDENCIES: react, ../api/client
 * PUBLIC API: useAccountActions, SaveToAccountOptions
 * SIDE EFFECTS: API calls, browser download
 * AGENT SAFETY: SAFE
 * Recent: 2026-02-07 - downloadBlob, sanitizeFilename, generateFilename moved outside hook (stale closure fix)
 */

import { useState, useCallback } from 'react';
import { accountsApi, salesIntelligenceApi } from '../api/client';

export interface SaveToAccountOptions {
  insightId: string;
  accountName?: string;
  tags?: string[];
}

/** Triggers browser download from Blob. */
const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const sanitizeFilename = (name: string): string =>
  name
    .replace(/[^a-z0-9]/gi, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .substring(0, 50);

const generateFilename = (
  insightId: string,
  companyName: string | undefined,
  extension: string
): string => {
  const date = new Date().toISOString().slice(0, 10);
  if (companyName) {
    const sanitized = sanitizeFilename(companyName);
    return `${sanitized}_research_${date}.${extension}`;
  }
  return `research_${insightId.slice(0, 8)}_${date}.${extension}`;
};

export function useAccountActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveToAccount = useCallback(async (options: SaveToAccountOptions) => {
    setLoading(true);
    setError(null);
    try {
      const result = await accountsApi.save({
        insightId: options.insightId,
        accountName: options.accountName,
        tags: options.tags
      });
      return result;
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : null;
      const errorMsg = msg || 'Failed to save to account';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  const exportToPDF = useCallback(async (insightId: string, companyName?: string) => {
    setLoading(true);
    setError(null);
    try {
      const blob = await salesIntelligenceApi.exportToPDF(insightId);
      downloadBlob(blob, generateFilename(insightId, companyName, 'pdf'));
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data
          : null;
      const detail = msg && typeof msg === 'object' && 'detail' in msg ? String(msg.detail) : null;
      const errorMsg = detail || 'Failed to export PDF';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  const exportToJSON = useCallback(async (insightId: string, companyName?: string) => {
    setLoading(true);
    setError(null);
    try {
      const blob = await salesIntelligenceApi.exportToJSON(insightId);
      downloadBlob(blob, generateFilename(insightId, companyName, 'json'));
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data
          : null;
      const detail = msg && typeof msg === 'object' && 'detail' in msg ? String(msg.detail) : null;
      const errorMsg = detail || 'Failed to export JSON';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  const exportToMarkdown = useCallback(async (insightId: string, companyName?: string) => {
    setLoading(true);
    setError(null);
    try {
      const blob = await salesIntelligenceApi.exportToMarkdown(insightId);
      downloadBlob(blob, generateFilename(insightId, companyName, 'md'));
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data
          : null;
      const detail = msg && typeof msg === 'object' && 'detail' in msg ? String(msg.detail) : null;
      const errorMsg = detail || 'Failed to export Markdown';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  const shareProfile = useCallback(async (insightId: string) => {
    setLoading(true);
    setError(null);
    try {
      const shareUrl = `${window.location.origin}/insights/${insightId}`;
      await navigator.clipboard.writeText(shareUrl);
    } catch (err: unknown) {
      const errorMsg = 'Failed to share profile';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    saveToAccount,
    exportToPDF,
    exportToJSON,
    exportToMarkdown,
    shareProfile,
    loading,
    error,
  };
}
